import { Raycaster } from 'three';
import { BaseClass } from 'dac-web-base';
import { DRAWING_TYPE } from './sceneZoneUnit';

export class SceneZoneMouse extends BaseClass {
	
	#rayCaster = null;

	constructor() {
		super();
		this.sceneUnit = null;
		this.container = null;
		this.#rayCaster = new Raycaster();

		this._enabled = true;
	}

	init( sceneUnit, container ) {
		this.sceneUnit = sceneUnit;
		this.container = container;

		container.addEventListener( 'pointerup', async( e ) => await this.#onMouseUp( e ) );
		container.addEventListener( 'pointerdown', async( e ) => await this.#onMouseDown( e ) );
		container.addEventListener( 'pointermove', async( e ) => await this.#onMouseMove( e ) );

		return this;
	}

	setEnabled( enabled ) {
		this._enabled = enabled;
		this.sceneUnit.controls.enabled = enabled;
	}

	async #onMouseDown( e ) {

		if( !this._enabled ) return;

		//if not mouse left button click, return
		if( e.button !== 0 ) return;
		// if not clicked on canvas, return
		if( e.target !== this.sceneUnit.renderUnit.renderer.domElement ) return;

		this._isMouseDown = true;
		this._mousePos = { x: e.clientX, y: e.clientY };
	}

	async #onMouseUp( e ) {

		if( !this._enabled ) return;

		//if not mouse left button click, return
		if( e.button !== 0 ) return;
		// if not clicked on canvas, return
		if( e.target !== this.sceneUnit.renderUnit.renderer.domElement ) return;

		delete this._isMouseDown;

		const intersect = this.#intersect( e );

		if( this._dragging ) {
			//finish draggin
			delete this._dragging;

			await this.trigger( 'onObjectDragEnd', { e: e } );
			return;
		}
		if( intersect.type === DRAWING_TYPE.NONE ) await this.trigger( 'onFloorClicked' );
		else if( intersect.meshes.length > 0 ) await this.trigger( 'onObjectClicked', { e: e, intersect: intersect, type: intersect.type } );
	}

	async #onMouseMove( e ) {

		if( !this._enabled ) return;

		const intersect = this.#intersect( e );
		const meshes = intersect.meshes;
		//check if moving the mouse using a delta pixels
		if( this._isMouseDown ) {
			const delta = 5;
			if( Math.abs( this._mousePos.x - e.clientX ) > delta || Math.abs( this._mousePos.y - e.clientY ) > delta ) {
				this._dragging = true;
				await this.trigger( 'onObjectDrag', { e: e, intersect: intersect, type: intersect.type } );
				return;
			}
		}

		if( meshes.length > 0 ) await this.trigger( 'onObjectHover', { e: e, intersect: intersect, type: intersect.type } );
		else this.container.style.cursor = '';
	}

	#intersect( e ) {
		
		//add a margin to the mouse position. User usually clicks with the top of the mouse
		const mousemargin = 0;

		//get what has the user collided with
		const pos = {
			x :( ( e.clientX - mousemargin ) / window.innerWidth ) * 2 - 1,
			y: - ( ( e.clientY - mousemargin ) / window.innerHeight ) * 2 + 1
		};

		// calculate objects intersecting the picking ray
		this.#rayCaster.setFromCamera( pos, this.sceneUnit.camera );

		let meshes = [];

		//against cells
		meshes = this.#intersectArray( this.sceneUnit._gridData.cells.children );
		if( meshes.length > 0 )
			return { type: DRAWING_TYPE.CELL, meshes: meshes };
		//against general dimensions
		meshes = this.#intersectArray( this.sceneUnit._gridData.dimensions.children );
		if( meshes.length > 0 ) 
			return { type: DRAWING_TYPE.GENERAL_DIMENSION, meshes: meshes };
		//against row dimensions
		meshes = this.#intersectArray( this.sceneUnit._gridData.rows.children );
		if( meshes.length > 0 )
			return { type: DRAWING_TYPE.ROW, meshes: meshes };
		//against column dimensions
		meshes = this.#intersectArray( this.sceneUnit._gridData.columns.children );
		if( meshes.length > 0 )
			return { type: DRAWING_TYPE.COLUMN, meshes: meshes };

		return { type: DRAWING_TYPE.NONE, meshes: [] };
	}

	#intersectArray( array ) {
		const intersects = this.#rayCaster.intersectObjects( array );

		// avoid LineSegments
		const meshes = intersects.filter( ( i ) => !this.#isEmpty( i.object ) && this.#isVisible( i.object ) );
		if( meshes.length > 0 ) return meshes;

		return [];
	}

	#isEmpty( obj ) {
		for (const prop in obj) {
			if ( Object.hasOwn( obj, prop ) ) {
				return false;
			}
		}
 
		return true;
	}

	#isVisible( obj ) {
		if( !obj ) return true;
		if( !obj.visible ) return false;
		let visible = true;
		obj.traverseAncestors( c => {
			if( !c ) return;
			if( !c.visible ) visible = false;
		} );

		return visible;
	}
}